import { RefObject } from "react";

/**
 * Finds the scrollable container element, checking the container itself
 * and traversing up the DOM tree to find the nearest scrollable parent.
 * Specifically looks for elements with overflow-y scroll/auto or max-height constraints.
 */
export function getScrollElement(containerRef: RefObject<HTMLElement>): HTMLElement | null {
	const container = containerRef.current;
	if (!container) return null;

	// Check if this element is scrollable
	const style = window.getComputedStyle(container);
	if (style.overflowY === "auto" || style.overflowY === "scroll" || style.overflow === "auto" || style.overflow === "scroll") {
		return container;
	}

	// Otherwise, find the nearest scrollable parent
	// Prioritize elements with overflow-y scroll/auto, then check for max-height constraints
	let parent = container.parentElement;
	while (parent) {
		const parentStyle = window.getComputedStyle(parent);
		
		// Check for explicit overflow-y scroll/auto (highest priority)
		if (
			parentStyle.overflowY === "auto" ||
			parentStyle.overflowY === "scroll" ||
			parentStyle.overflow === "auto" ||
			parentStyle.overflow === "scroll"
		) {
			return parent;
		}
		
		// Check for max-height constraint (indicates scrollable container)
		if (
			parentStyle.maxHeight !== "none" &&
			parentStyle.maxHeight !== "" &&
			parseInt(parentStyle.maxHeight, 10) > 0
		) {
			// Verify it's actually scrollable by checking scrollHeight vs clientHeight
			if (parent.scrollHeight > parent.clientHeight) {
				return parent;
			}
		}
		
		// Check for .events-container class specifically (common pattern in this codebase)
		if (parent.classList.contains("events-container")) {
			return parent;
		}
		
		parent = parent.parentElement;
	}

	// Fallback to container itself
	return container;
}

/**
 * Finds the nearest scrollable ancestor of an element (overflow-y: auto/scroll).
 * Used for sticky header when the page scrolls inside a div rather than the window.
 */
export function findScrollParent(element: HTMLElement | null): HTMLElement | null {
	if (!element) return null;
	let parent = element.parentElement;
	while (parent) {
		const style = window.getComputedStyle(parent);
		
		// Check for explicit overflow-y scroll/auto
		if (
			style.overflowY === "auto" ||
			style.overflowY === "scroll" ||
			style.overflow === "auto" ||
			style.overflow === "scroll"
		) {
			return parent;
		}
		
		// Check for .events-container class specifically
		if (parent.classList.contains("events-container")) {
			return parent;
		}
		
		// Check for max-height constraint (indicates scrollable container)
		if (
			style.maxHeight !== "none" &&
			style.maxHeight !== "" &&
			parseInt(style.maxHeight, 10) > 0 &&
			parent.scrollHeight > parent.clientHeight
		) {
			return parent;
		}
		
		parent = parent.parentElement;
	}
	return null;
}

/**
 * Scrolls an element into view within its scroll container (not the viewport).
 * This is necessary when the scroll container is not the window/body.
 */
export function scrollIntoViewInContainer(
	element: HTMLElement,
	scrollContainer: HTMLElement,
	options: { behavior?: ScrollBehavior; block?: ScrollLogicalPosition } = {}
): void {
	const { behavior = "smooth", block = "center" } = options;

	const elementRect = element.getBoundingClientRect();
	const containerRect = scrollContainer.getBoundingClientRect();

	// Calculate the element's position relative to the container
	const elementTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
	const elementBottom = elementTop + elementRect.height;
	const containerHeight = scrollContainer.clientHeight;

	let scrollTop: number;

	switch (block) {
		case "start":
			scrollTop = elementTop;
			break;
		case "end":
			scrollTop = elementBottom - containerHeight;
			break;
		case "center":
		default:
			scrollTop = elementTop - containerHeight / 2 + elementRect.height / 2;
			break;
	}

	scrollContainer.scrollTo({
		top: Math.max(0, scrollTop),
		behavior,
	});
}
